import { createStore, del, get, set } from 'idb-keyval'
import * as Json from 'ox/Json'

import {
  migrateCookies,
  migrateIndexedDb,
  migrateLocalStorage,
  removeCookie,
} from './internal/compat/browserStorage.js'
import {
  inheritStorageCompatibility,
  inheritStorageNormalizer,
  inheritStorageOperationScope,
  storageValuesEqualFor,
  withLegacyFallback,
  withStorageNormalizer,
  withStorageOperationScope,
} from './internal/compat/storage.js'
import type { MaybePromise } from './internal/types.js'
import * as Utils from './internal/utils.js'

const combinedStorageOperationScope = {}
const indexedDbOperationScope = {}

export type Storage = {
  getItem: <value>(name: string) => MaybePromise<value | null>
  removeItem: (name: string) => MaybePromise<void>
  setItem: (name: string, value: unknown) => MaybePromise<void>
  sizeLimit: number
  storages?: readonly Storage[] | undefined
}

export function from(storage: Storage): Storage {
  return storage
}

export function combine(...storages: readonly Storage[]): Storage {
  const storage = from({
    async getItem<value>(name: string) {
      const results = await Promise.allSettled(
        storages.map(async (x) => x.getItem(name)),
      )
      const result = results.find(
        (x) => x.status === 'fulfilled' && x.value != null,
      )
      if (result?.status !== 'fulfilled') return null
      if (result.value == null) return null
      return result.value as value
    },
    async removeItem(name) {
      await Promise.allSettled(storages.map(async (x) => x.removeItem(name)))
    },
    async setItem(name, value) {
      const writes = await Promise.allSettled(
        storages.map(async (x) => x.setItem(name, value)),
      )
      const verified = await Promise.all(
        storages.map(async (storage, index) => {
          if (writes[index]?.status !== 'fulfilled') return false
          try {
            const written = await storage.getItem(name)
            return (
              written != null && storageValuesEqualFor(storage, written, value)
            )
          } catch {
            return false
          }
        }),
      )

      // Preserve every existing copy when no backend proves the new write.
      if (!verified.some(Boolean)) return

      await Promise.allSettled(
        storages.map(async (storage, index) => {
          if (!verified[index]) await storage.removeItem(name)
        }),
      )
    },
    sizeLimit: Math.min(...storages.map((x) => x.sizeLimit)),
    storages,
  })
  inheritStorageNormalizer(storage, ...storages)
  return withStorageOperationScope(storage, combinedStorageOperationScope)
}

export function idb() {
  const storage = withStorageOperationScope(
    indexedDbStore('levery'),
    typeof indexedDB === 'undefined' ? indexedDbOperationScope : indexedDB,
  )
  return withMigration(withLegacyFallback(storage), () =>
    migrateIndexedDb(indexedDB),
  )
}

function indexedDbStore(databaseName: string) {
  const store =
    typeof indexedDB !== 'undefined'
      ? createStore(databaseName, 'store')
      : undefined
  return from({
    async getItem(name) {
      const value = await get(name, store)
      if (value == null) return null
      return value
    },
    async removeItem(name) {
      await del(name, store)
    },
    async setItem(name, value) {
      await set(name, Utils.normalizeValue(value), store)
    },
    sizeLimit: 1024 * 1024 * 50, // ≈50MB
  })
}

export function localStorage() {
  const storage = withStorageOperationScope(
    withStorageNormalizer(
      from({
        async getItem(name) {
          const item = window.localStorage.getItem(name)
          if (item === null) return null
          try {
            return Json.parse(item)
          } catch {
            return null
          }
        },
        async removeItem(name) {
          window.localStorage.removeItem(name)
        },
        async setItem(name, value) {
          window.localStorage.setItem(name, Json.stringify(value))
        },
        sizeLimit: 1024 * 1024 * 5, // ≈5MB
      }),
      normalizeJsonStorageValue,
    ),
    window.localStorage,
  )
  return withMigration(withLegacyFallback(storage), () =>
    migrateLocalStorage(window.localStorage),
  )
}

export function cookie() {
  const storage = withStorageOperationScope(
    withStorageNormalizer(
      from({
        async getItem(name) {
          const value = document.cookie
            .split('; ')
            .find((x) => x.startsWith(`${name}=`))
          if (!value) return null
          try {
            return Json.parse(value.substring(name.length + 1))
          } catch {
            return null
          }
        },
        async removeItem(name) {
          removeCookie(document, name)
        },
        async setItem(name, value) {
          // biome-ignore lint/suspicious/noDocumentCookie: do what i want
          document.cookie = `${name}=${Json.stringify(value)};path=/;samesite=None;secure;max-age=31536000`
        },
        sizeLimit: 1024 * 4, // ≈4kB
      }),
      normalizeJsonStorageValue,
    ),
    document,
  )
  return withMigration(withLegacyFallback(storage), () =>
    migrateCookies(document),
  )
}

function withMigration(
  storage: Storage,
  migrate: () => Promise<void>,
): Storage {
  let migration: Promise<void> | undefined
  const ready = () =>
    (migration ??= migrate().catch((error) => {
      migration = undefined
      throw error
    }))
  const attemptMigration = async () => {
    try {
      await ready()
    } catch {}
  }

  const migrated = from({
    async getItem<value>(name: string) {
      await attemptMigration()
      return storage.getItem<value>(name)
    },
    async removeItem(name) {
      await attemptMigration()
      await storage.removeItem(name)
    },
    async setItem(name, value) {
      await attemptMigration()
      await storage.setItem(name, value)
    },
    sizeLimit: storage.sizeLimit,
    storages: storage.storages,
  })
  inheritStorageCompatibility(migrated, storage)
  inheritStorageNormalizer(migrated, storage)
  return inheritStorageOperationScope(migrated, storage)
}

function normalizeJsonStorageValue(value: unknown) {
  return Json.parse(Json.stringify(value))
}

export function memory() {
  const store = new Map<string, any>()
  return from({
    getItem(name) {
      return store.get(name) ?? null
    },
    removeItem(name) {
      store.delete(name)
    },
    setItem(name, value) {
      store.set(name, value)
    },
    sizeLimit: Number.POSITIVE_INFINITY,
  })
}
